// src/components/sections/ProductFeatures.jsx
import React from 'react';
import { useTheme } from '../../context/ThemeContext';

const ProductFeatures = () => {
  const { isDark } = useTheme();
  
  const features = [
    {
      title: 'Automated Workflows',
      icon: '⚡',
      description: 'Transform your business operations with intelligent automation',
      details: [
        'Streamline complex operations across departments',
        'Automate repetitive and time-consuming tasks',
        'Significantly reduce human error rates',
        'Boost overall productivity and efficiency',
        'Improve cross-departmental collaboration'
      ]
    },
    {
      title: 'Custom Integrations',
      icon: '🔌',
      description: 'Seamlessly connect with your existing technology stack',
      details: [
        'Integrate smoothly with your current systems',
        'Customize software to match your unique processes',
        'Scale solutions alongside your business growth',
        'API-first approach for maximum flexibility',
        'Connect third-party tools and platforms'
      ]
    },
    {
      title: 'Data Analytics',
      icon: '📊',
      description: 'Turn raw data into actionable business intelligence',
      details: [
        'In-depth market trend analysis',
        'Customer behavior insights and patterns',
        'Real-time analytics dashboards',
        'Data-driven decision support tools',
        'Predictive modeling capabilities'
      ]
    },
    {
      title: 'Scalable Solutions',
      icon: '📈',
      description: 'Build for growth with future-proof technology',
      details: [
        'Support seamless business expansion',
        'Enable cost-effective scaling strategies',
        'Adapt quickly to technological advancements',
        'Future-proof your operations',
        'Flexible architecture for evolving needs'
      ]
    }
  ];
  
  const offerings = [
    {
      title: 'Custom Software Development',
      icon: '💻',
      description: 'Tailored solutions for unique business challenges',
      benefits: [
        'Address specific operational needs',
        'Solve unique business challenges',
        'Built with your requirements in mind',
        'Agile development methodology',
        'Full-cycle development support'
      ]
    },
    {
      title: 'Management System Consulting',
      icon: '📋',
      description: 'Strategic optimization of business processes',
      benefits: [
        'Optimize workflow efficiency',
        'Implement best-practice management systems',
        'Streamline organizational processes',
        'Enhance operational transparency',
        'Drive continuous improvement'
      ]
    },
    {
      title: 'Ecommerce Applications',
      icon: '🛒',
      description: 'Powerful digital commerce solutions',
      benefits: [
        'Deliver seamless shopping experiences',
        'Integrate with existing business processes',
        'Expand digital market opportunities',
        'Mobile-optimized storefronts',
        'Secure payment gateways'
      ]
    },
    {
      title: 'Software Licensing',
      icon: '📜',
      description: 'Flexible licensing for ready-made products',
      benefits: [
        'Ready-made products for quick deployment',
        'Adaptable to different business requirements',
        'Cost-effective licensing models',
        'Regular updates and support',
        'Enterprise-grade security included'
      ]
    }
  ];
  
  return (
    <section className={`relative py-24 sm:py-32 overflow-hidden ${
      isDark 
        ? 'bg-gradient-to-b from-black to-blue-950/30' 
        : 'bg-gradient-to-b from-blue-50 to-white'
    }`}>
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-0 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-700/20 rounded-full blur-3xl" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Product Features Section */}
        <div className="mb-32">
          <div className="text-center mb-16">
            
            <div className="mb-4">
              <span className={`text-xs sm:text-sm tracking-[0.2em] font-medium px-4 py-2 rounded-full inline-block ${
                isDark 
                  ? 'text-blue-400 bg-blue-600/15 border border-blue-500/20' 
                  : 'text-blue-700 bg-blue-100 border border-blue-200'
              }`}>
                PRODUCT FEATURES
              </span>
            </div>

            <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Powerful features for
              <br />
              <span className="bg-gradient-to-r from-blue-500 via-blue-600 to-blue-800 bg-clip-text text-transparent">
                modern business
              </span>
            </h2>

            <p className={`max-w-2xl mx-auto ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Discover how our enterprise-grade features can transform your business operations
              and drive sustainable growth
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`group relative backdrop-blur-sm rounded-2xl p-8 border transition-all duration-300 hover:transform hover:scale-[1.02] ${
                  isDark 
                    ? 'bg-gradient-to-br from-blue-600/20 to-blue-900/20 border-white/10 hover:border-blue-500/40'
                    : 'bg-gradient-to-br from-blue-100/70 to-blue-50 border-blue-200 hover:border-blue-500'
                }`}
              >
                
                {/* Glow effect on hover - only in dark mode */}
                {isDark && (
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-blue-600/10 to-blue-800/10" />
                )}
                
                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    
                    <div className="text-5xl">
                      {feature.icon}
                    </div>

                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                      isDark 
                        ? 'bg-blue-600/15 group-hover:bg-blue-600/25' 
                        : 'bg-blue-100 group-hover:bg-blue-200'
                    }`}>
                      <svg 
                        className={`w-6 h-6 ${
                          isDark ? 'text-blue-400' : 'text-blue-700'
                        }`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M13 7l5 5m0 0l-5 5m5-5H6" 
                        />
                      </svg>
                    </div>
                  </div>
                  
                  <h3 className={`text-2xl font-bold mb-2 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {feature.title}
                  </h3>

                  <p className={`text-sm mb-4 ${
                    isDark ? 'text-blue-400' : 'text-blue-700'
                  }`}>
                    {feature.description}
                  </p>
                  
                  <ul className="space-y-2 mt-4">
                    {feature.details.map((detail, idx) => (
                      <li 
                        key={idx} 
                        className={`flex items-start gap-2 text-sm ${
                          isDark ? 'text-gray-300' : 'text-gray-600'
                        }`}
                      >
                        <svg 
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                            isDark ? 'text-blue-400' : 'text-blue-700'
                          }`} 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path 
                            fillRule="evenodd" 
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                            clipRule="evenodd" 
                          />
                        </svg>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Core Offerings Section */}
        <div>
          <div className="text-center mb-16">
            
            <div className="mb-4">
              <span className={`text-xs sm:text-sm tracking-[0.2em] font-medium px-4 py-2 rounded-full inline-block ${
                isDark 
                  ? 'text-blue-400 bg-blue-600/15 border border-blue-500/20' 
                  : 'text-blue-700 bg-blue-100 border border-blue-200'
              }`}>
                CORE OFFERINGS
              </span>
            </div>

            <h2 className={`text-3xl sm:text-4xl font-bold mb-6 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              What we deliver
            </h2>

            <p className={`max-w-2xl mx-auto ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Comprehensive solutions designed to address your most pressing business challenges
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {offerings.map((offering, index) => (
              <div 
                key={index} 
                className={`group backdrop-blur-sm rounded-2xl p-6 border transition-all duration-300 ${
                  isDark 
                    ? 'bg-white/5 border-white/10 hover:border-blue-500/40 hover:bg-blue-900/10'
                    : 'bg-blue-50 border-blue-200 hover:border-blue-500 hover:bg-blue-100/60'
                }`}
              >
                
                <div className="text-4xl mb-4">
                  {offering.icon}
                </div>

                <h3 className={`text-xl font-semibold mb-2 transition-colors ${
                  isDark 
                    ? 'text-white group-hover:text-blue-400' 
                    : 'text-gray-900 group-hover:text-blue-700'
                }`}>
                  {offering.title}
                </h3>

                <p className={`text-sm mb-4 ${
                  isDark ? 'text-blue-400' : 'text-blue-700'
                }`}>
                  {offering.description}
                </p>
                
                <ul className="space-y-2">
                  {offering.benefits.map((benefit, idx) => (
                    <li 
                      key={idx} 
                      className={`flex items-start gap-2 text-xs ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      <svg 
                        className={`w-3 h-3 mt-0.5 flex-shrink-0 ${
                          isDark ? 'text-blue-400' : 'text-blue-700'
                        }`} 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        <path 
                          fillRule="evenodd" 
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                          clipRule="evenodd" 
                        />
                      </svg>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-700/40 to-transparent" />
    </section>
  );
};

export default ProductFeatures;